import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = (html: string): string => {
    if (!html) return '';
    return DOMPurify.sanitize(html);
};

/**
 * Decodes HTML entities in a string and sanitizes the result
 * @param html - The HTML string to decode
 * @returns The decoded and sanitized string
 */
export const decodeHtmlEntities = (html: string): string => {
    if (!html) return '';

    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return DOMPurify.sanitize(textarea.value);
};

/**
 * Safely renders HTML content with proper sanitization
 * @param html - The HTML string to render
 * @returns An object with dangerouslySetInnerHTML property
 */
export const renderHtmlContent = (html: string) => {
    return {
        dangerouslySetInnerHTML: {
            __html: sanitizeHtml(html)
        }
    };
};

/**
 * Truncates HTML content while preserving HTML tags
 * @param html - The HTML string to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated HTML string
 */
export const truncateHtmlContent = (html: string, maxLength: number): string => {
    if (!html) return '';
    
    const decoded = decodeHtmlEntities(html);
    if (decoded.length <= maxLength) {
        return html;
    }
    
    // For HTML content, we'll use a simple approach:
    // 1. Decode HTML entities to get plain text
    // 2. Truncate the plain text
    // 3. Return the truncated plain text (safe for display)
    
    const truncated = decoded.substring(0, maxLength) + '...';
    return truncated;
}; 